import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { motion } from 'framer-motion';
import { Building2, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

type PageState = 'loading' | 'ready' | 'joining' | 'success' | 'error';

export default function JoinStudio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session, loading } = useAuth();
  const { refetch } = useStudio();
  const token = searchParams.get('token') ?? '';

  const [state, setState] = useState<PageState>('loading');
  const [studioName, setStudioName] = useState('');
  const [role, setRole] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (loading) return;
    if (!user && !session) {
      navigate(`/auth/login?redirect=${encodeURIComponent(`/join?token=${token}`)}`, { replace: true });
    }
  }, [user, session, loading, token, navigate]);

  useEffect(() => {
    if (!token || !user) return;
    supabase
      .from('studio_invitations')
      .select('studio_id, role, expires_at, used_at, studios(name)')
      .eq('token', token)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) { setErrorMsg('Invitation introuvable.'); setState('error'); return; }
        if (data.used_at) { setErrorMsg('Ce lien a déjà été utilisé.'); setState('error'); return; }
        if (new Date(data.expires_at) < new Date()) { setErrorMsg('Ce lien a expiré (48h).'); setState('error'); return; }
        setStudioName((data.studios as any)?.name ?? '');
        setRole(data.role);
        setState('ready');
      });
  }, [token, user?.id]);

  const handleJoin = async () => {
    setState('joining');
    try {
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession?.access_token) {
        setErrorMsg('Session expirée, veuillez vous reconnecter.');
        setState('error');
        return;
      }
      const { data, error } = await supabase.functions.invoke('validate-invitation', {
        body: { inviteToken: token },
        headers: { Authorization: `Bearer ${freshSession.access_token}` },
      });

      if (error || (data as any)?.error) {
        setErrorMsg((data as any)?.error ?? error?.message ?? 'Erreur inconnue');
        setState('error');
        return;
      }

      refetch();
      setState('success');
      setTimeout(() => navigate('/'), 2000);
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Erreur inconnue');
      setState('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm w-full text-center space-y-6">
        {(state === 'loading' || state === 'joining') && (
          <>
            <Loader2 size={40} className="text-primary animate-spin mx-auto" />
            <p className="text-muted-foreground">{state === 'joining' ? 'Rejoindre le studio…' : 'Vérification…'}</p>
          </>
        )}
        {state === 'ready' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
              <Building2 size={28} className="text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold font-display">Rejoindre le studio</h2>
              <p className="text-sm text-muted-foreground">
                Vous avez été invité à rejoindre <strong>{studioName}</strong> en tant que <strong>{role === 'guest' ? 'Guest' : 'Résident'}</strong>.
              </p>
            </div>
            <button onClick={handleJoin} className="w-full py-3.5 bg-primary text-primary-foreground rounded-2xl font-semibold hover:bg-primary/90 transition-colors">
              Rejoindre {studioName}
            </button>
            <button onClick={() => navigate('/')} className="text-sm text-muted-foreground hover:text-foreground">Annuler</button>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto">
              <CheckCircle2 size={28} className="text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold font-display">Bienvenue !</h2>
            <p className="text-sm text-muted-foreground">Vous avez rejoint <strong>{studioName}</strong>. Redirection…</p>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertTriangle size={28} className="text-destructive" />
            </div>
            <h2 className="text-2xl font-bold font-display">Lien invalide</h2>
            <p className="text-sm text-muted-foreground">{errorMsg}</p>
            <button onClick={() => navigate('/')} className="w-full py-3 bg-secondary text-secondary-foreground rounded-xl font-semibold">Retour</button>
          </>
        )}
      </motion.div>
    </div>
  );
}
